<template>
  <IconField icon-position="right" class="client-multi-select-input-container">
    <InputChips
      :model-value="selectedDomainNames"
      :input-id="id"
      :loading="clients.loading"
      :disabled="!!disabled"
      :placeholder="t('clients.clients', 2)"
      class="client-multi-select"
      separator=","
      add-on-blur
      @click="showPopover"
      @focus="showPopover"
      @remove="onRemove"
    />
    <InputIcon class="pi pi-chevron-down" />
  </IconField>

  <Popover ref="popover" class="client-multi-select-popover">
    <div class="popover-filter-container">
      <InputSearch
        v-model="filterText"
        icon-type="icon"
        class="popover-filter"
      />
      <RygenButton
        v-if="filterText"
        mode="tertiary"
        icon="pi pi-times"
        @click="filterText = ''"
      />
    </div>

    <DomainTreeMultiSelect
      v-model="internalValue"
      :options="effectiveDomainOptions"
    />
  </Popover>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, onMounted, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { DomainReference, DomainTree, Optional } from '@rygen/rygen-ui/model';
import { filter, notNull } from '@rygen/rygen-ui/util';

import { useClientStore } from '@/stores/Client';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import DomainTreeMultiSelect from '@rygen/rygen-ui/components/domain/DomainTreeMultiSelect.vue';
import InputSearch from '@rygen/rygen-ui/components/form/InputSearch.vue';
import IconField from 'primevue/iconfield';
import InputChips, { InputChipsRemoveEvent } from 'primevue/inputchips';
import InputIcon from 'primevue/inputicon';
import Popover from 'primevue/popover';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array as PropType<DomainReference[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: DomainReference[]): void;
}>();

const { clients, loadClients } = useClientStore();
const { t } = useI18n();

const popover: Ref<any | null> = ref(null);
const internalValue: Ref<DomainReference[]> = ref([]);
const filterText = ref('');

const effectiveDomainOptions = computed<DomainTree[]>(() => {
  return filter(clients.value.data ?? [], filterText.value);
});

const selectedDomainNames = computed(() => {
  return internalValue.value.map((d) => d.name).filter(notNull);
});

const showPopover = (event: Event) => {
  popover.value?.show(event);
  nextTick(() => {
    const container = document.querySelector(
      '.client-multi-select-input-container',
    ) as HTMLElement;

    const popover = document.querySelector(
      '.client-multi-select-popover',
    ) as HTMLElement;

    const popoverFilter = popover.querySelector(
      '.p-inputtext[type="search"]',
    ) as Optional<HTMLElement>;
    popoverFilter?.focus();

    const rect = container.getBoundingClientRect();
    popover.style.left = `${rect.left}px`;
  });
};

const onRemove = (event: InputChipsRemoveEvent) => {
  // Why tf does PrimeVue emit an array when the event only supports a single element
  const value = Array.isArray(event.value) ? event.value[0] : event.value;
  const index = internalValue.value.findIndex((d) => d.name === value);
  if (index !== -1) {
    internalValue.value.splice(index, 1);
  }
};

watch(
  modelValue,
  (newVal: DomainReference[]) => {
    internalValue.value = newVal.map((d) => new DomainReference(d));
  },
  { deep: true, immediate: true },
);

watch(
  internalValue,
  (newVal: DomainReference[]) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);

onMounted(() => {
  loadClients();
});
</script>

<style>
.client-multi-select-input-container {
  .p-inputchips {
    max-width: 100%;
    width: 100%;

    .p-inputchips-input {
      flex-wrap: nowrap;
      overflow: auto;

      .p-inputchips-chip-item {
        flex: 1 0 auto;
      }

      .p-inputchips-input-item {
        width: 85px;
      }
    }
  }

  .p-inputicon {
    pointer-events: none;
  }
}

.client-multi-select-popover.p-popover {
  --p-popover-content-padding: 0;

  max-height: 300px;
  overflow: auto;

  &::before,
  &::after {
    content: none;
  }

  .popover-filter-container {
    display: flex;
    gap: var(--gap-1);
    background-color: var(--p-surface-100);
    padding: var(--sp-3);
    box-shadow: var(--elevation);
  }
}
</style>
